import { saveAs } from 'file-saver';
import type { Connector, ConnectorType, ConnectorStatus, IngestJob } from '../types/domain';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000').replace(/\/$/, '');
const AUTH_TOKEN_KEY = 'simuladesk_access_token';

export type Entity =
  | 'audit-groups'
  | 'audits'
  | 'goals'
  | 'operators'
  | 'supervisors'
  | 'sectors'
  | 'monitors'
  | 'scenarios'
  | 'questionnaires'
  | 'questionnaire-responses'
  | 'training-sessions'
  | 'training-events'
  | 'certificates'
  | 'leagues'
  | 'league-participants'
  | 'seasons'
  | 'users'
  | 'notifications'
  | 'scorecard'
  | 'feedback'
  | 'feedbacks'
  | 'scripts'
  | 'badges'
  | 'operator-badges'
  | 'dashboard-widgets'
  | 'monitor-goals'
  | 'monitor-performance'
  | 'audit-queue'
  | 'supervisor-alerts'
  | 'pending-approvals'
  | 'team-weekly-trends'
  | 'team-activities'
  | 'coaching-notes'
  | 'hierarchy-history'
  | 'tenants'
  | 'tenant-activity-logs'
  | 'white-label-configs'
  | 'comments'
  | 'rankings';

interface ApiResponse<T> {
  data?: T;
  count?: number;
  error?: string;
  status?: string;
  [key: string]: any;
}

interface PaginatedApiResponse<T> extends ApiResponse<T[]> {
  page?: number;
  pageSize?: number;
  totalPages?: number;
  totalCount?: number;
}

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAccessToken(token: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function hasAccessToken(): boolean {
  return Boolean(getAccessToken());
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  authRequired = true,
): Promise<ApiResponse<T>> {
  try {
    const accessToken = getAccessToken();

    if (authRequired && !accessToken) {
      return { error: 'Sessao invalida. Faca login novamente.' };
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...options.headers,
      },
    });

    const json = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        clearAccessToken();
      }
      return { error: json.error || `Erro ${response.status}`, ...json };
    }
    return json;
  } catch (error) {
    return { error: `Erro de rede: ${error}` };
  }
}

function toQueryString(query?: Record<string, string | number | boolean>): string {
  if (!query) return '';
  const entries = Object.entries(query).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return entries.length > 0 ? `?${entries.join('&')}` : '';
}

export async function authenticate(
  email: string,
  password: string,
): Promise<{ accessToken: string; user: any } | null> {
  const result = await request<{ accessToken: string; user: any }>(
    '/api/auth/login',
    {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    },
    false,
  );

  if (!result.data?.accessToken || !result.data?.user) {
    return null;
  }

  setAccessToken(result.data.accessToken);
  return result.data;
}

export async function listEntities<T>(entity: Entity, query?: Record<string, string | number | boolean>): Promise<T[]> {
  const collected: T[] = [];
  const initialQuery = { ...(query || {}), page: Number(query?.page || 1) };

  while (true) {
    const qs = toQueryString(initialQuery);
    const result = await request<T[]>(`/api/${entity}${qs}`) as PaginatedApiResponse<T>;
    if (result.error) {
      return collected;
    }

    const rows = result.data || [];
    collected.push(...rows);

    const currentPage = Number(result.page || initialQuery.page || 1);
    const totalPages = Number(result.totalPages || 1);
    if (!Number.isFinite(totalPages) || currentPage >= totalPages) {
      break;
    }
    initialQuery.page = currentPage + 1;
  }

  return collected;
}

export async function getEntity<T>(
  entity: Entity,
  id: string,
  query?: Record<string, string | number | boolean>,
): Promise<T | null> {
  const qs = toQueryString(query);
  const result = await request<T>(`/api/${entity}/${id}${qs}`);
  return result.data || null;
}

export async function saveEntity<T extends { id?: string | number }>(entity: Entity, data: T): Promise<T | null> {
  const id = data.id;
  const result = id
    ? await request<T>(`/api/${entity}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    : await request<T>(`/api/${entity}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  if (result.error) {
    throw new Error(result.error);
  }
  return result.data || null;
}

export async function createEntity<T>(entity: Entity, data: T): Promise<T | null> {
  const result = await request<T>(`/api/${entity}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.data || null;
}

export async function saveBatch<T extends { id?: string | number }>(entity: Entity, items: T[]): Promise<number> {
  const saved = await Promise.all(items.map((item) => saveEntity(entity, item)));
  return saved.filter(Boolean).length;
}

export async function deleteEntity(entity: Entity, id: string): Promise<boolean> {
  const result = await request<void>(`/api/${entity}/${id}`, {
    method: 'DELETE',
  });
  return result.status === 'ok';
}

// Same DELETE call, but surfaces the backend error message instead of dropping it.
export async function deleteEntityResult(
  entity: Entity,
  id: string,
): Promise<{ ok: boolean; error?: string }> {
  const result = await request<void>(`/api/${entity}/${id}`, {
    method: 'DELETE',
  });
  if (result.status === 'ok') return { ok: true };
  return { ok: false, error: result.error };
}

export async function postAction<T = any>(
  path: string,
  body: Record<string, unknown>,
  authRequired = true,
): Promise<T | null> {
  const result = await request<T>(
    path,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    authRequired,
  );
  return result.data || null;
}

export async function healthCheck(): Promise<boolean> {
  const result = await request<any>('/health', {}, false);
  return result.status === 'ok';
}

export async function getMe<T = any>(): Promise<T | null> {
  const result = await request<T>('/api/me');
  return result.data || null;
}

export async function getSettings(): Promise<any | null> {
  const result = await request<any>('/api/settings');
  return result.data || null;
}

export interface TenantSettings {
  tenantId: string;
  auditValidationDeadlineHours: number;
  auditValidationAlertHours: number;
  enableDeadlineAlerts: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export async function getTenantSettings(): Promise<TenantSettings | null> {
  const result = await request<TenantSettings>('/api/tenant-settings');
  return result.data || null;
}

export async function saveTenantSettings(payload: {
  auditValidationDeadlineHours: number;
  auditValidationAlertHours: number;
  enableDeadlineAlerts: boolean;
}): Promise<ApiResponse<TenantSettings>> {
  return request<TenantSettings>('/api/tenant-settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function saveSettingsProfile(payload: {
  name: string;
  email: string;
  phone?: string;
  bio?: string;
  avatar?: string | null;
}): Promise<ApiResponse<any>> {
  return request<any>('/api/settings/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function changePassword(payload: {
  currentPassword: string;
  newPassword: string;
}): Promise<ApiResponse<any>> {
  return request<any>('/api/settings/password', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function saveNotificationSettings(payload: {
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyReports: boolean;
  auditAlerts: boolean;
}): Promise<ApiResponse<any>> {
  return request<any>('/api/settings/notifications', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function savePreferenceSettings(payload: {
  language: string;
  timezone: string;
  theme: 'light' | 'dark';
}): Promise<ApiResponse<any>> {
  return request<any>('/api/settings/preferences', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export interface ResetPasswordOptions {
  customPassword?: string;
  sendEmail?: boolean;
  reason?: string;
}

export interface ResetPasswordResult {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  temporaryPassword: string;
  mustChangeOnLogin: boolean;
  emailSent: boolean;
  resetAt: string;
  resetBy: string;
  resetByName: string;
}

export interface TrainingTranscriptEntry {
  speaker: 'operator' | 'client';
  text: string;
  timestamp?: string | number;
}

export async function resetUserPassword(
  userId: string,
  options?: ResetPasswordOptions,
): Promise<ApiResponse<ResetPasswordResult>> {
  return request<ResetPasswordResult>(`/api/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(options ?? {}),
  });
}

export async function evaluateAudit(
  auditId: string,
  payload: {
    questionnaireId?: string;
    items: Array<{ scorecardItemId?: string; questionId?: string; passed: boolean; justification: string }>;
    positives?: string[];
    improvements?: string[];
    generalComments?: string;
    summary?: string;
    transcription?: string;
    preserveStatus?: boolean;
  },
) {
  const result = await request(`/api/audits/${auditId}/evaluate`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (result.error) {
    throw new Error(result.error);
  }
  return result.data || result;
}

export async function createAuditGroup(payload: {
  callId: string;
  operatorId: string;
  supervisorId?: string;
  sectorId: string;
  questionnaireId?: string;
  qualityMonitorId?: string;
  aiMonitorId?: string;
  date: string;
  duration: number;
  audioUrl?: string;
  summary?: string;
  generalComments?: string;
}) {
  return postAction('/api/audit-groups', payload);
}

export async function getAuditGroup(id: string) {
  const result = await request<any>(`/api/audit-groups/${id}`);
  return result.data || null;
}

export async function updateAuditGroup(id: string, payload: {
  questionnaireId?: string | null;
  aiMonitorId?: string | null;
  qualityMonitorId?: string | null;
}): Promise<any | null> {
  const result = await request<any>(`/api/audit-groups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result.data || null;
}

export async function createAuditFromGroup(
  auditGroupId: string,
  payload: {
    origin: 'qualidade' | 'supervisor' | 'ai';
    monitorId?: string;
    questionnaireId?: string;
    status?: string;
  },
) {
  return postAction(`/api/audit-groups/${auditGroupId}/audits`, payload);
}

export async function runAiAuditFromGroup(
  auditGroupId: string,
  payload?: {
    monitorId?: string;
    questionnaireId?: string;
  },
) {
  // run-ai returns {status:'queued',jobId,auditId} (HTTP 202) without a .data wrapper
  // Use raw request to get the full response object
  const result = await request<any>(
    `/api/audit-groups/${auditGroupId}/run-ai`,
    { method: 'POST', body: JSON.stringify(payload || {}) },
  );
  if (result.error) return null;
  // Return .data if wrapped, otherwise the raw result (BullMQ queued response)
  return result.data || result;
}

export async function evaluateTrainingSession(
  sessionId: string,
  payload: {
    items: Array<{ scorecardItemId: string; passed: boolean; justification: string }>;
    positives?: string[];
    improvements?: string[];
    duration?: number;
    transcription?: TrainingTranscriptEntry[] | string;
    recordingUrl?: string | null;
  },
) {
  return request(`/api/training-sessions/${sessionId}/evaluate`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function runAiTrainingEvaluation(
  sessionId: string,
): Promise<any | null> {
  const result = await request(`/api/training-sessions/${sessionId}/ai-evaluate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (result.error) {
    throw new Error(result.error);
  }
  return result.data || null;
}

export async function createDemoLead(payload: {
  name: string;
  email: string;
  phone: string;
  company: string;
  role?: string;
  scenarioId?: string;
}): Promise<{ id: string; name: string; email: string; company: string; sessionToken: string } | null> {
  const result = await request<{ id: string; name: string; email: string; company: string; sessionToken: string }>(
    '/api/demo/leads',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    false,
  );
  if (result.error) {
    throw new Error(result.error);
  }
  return result.data || null;
}

export async function uploadTrainingRecording(
  sessionId: string,
  file: File,
): Promise<{ url: string; filename: string; path: string } | null> {
  const token = getAccessToken();
  if (!token) return null;

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/training-sessions/${sessionId}/recording`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.error || `Erro ${response.status}`);
  }
  return json.data || null;
}

export async function getStatsOperators<T>(): Promise<T[]> {
  const result = await request<T[]>('/api/stats/operators');
  return result.data || [];
}

export async function getStatsRanking<T>(): Promise<T[]> {
  const result = await request<T[]>('/api/stats/ranking');
  return result.data || [];
}

export async function getStatsEvolution<T>(operatorId: string): Promise<T[]> {
  const result = await request<T[]>(`/api/stats/evolution/${operatorId}`);
  return result.data || [];
}

export async function getStatsSupervisorSummary<T>(): Promise<T[]> {
  const result = await request<T[]>('/api/stats/supervisor-summary');
  return result.data || [];
}

// ═══════════════════════════════════════════════════════════════
// Dashboard widgets API (F1)
// ═══════════════════════════════════════════════════════════════

export async function getUserDashboard<T = any>(): Promise<T[]> {
  const result = await request<T[]>('/api/user-dashboard');
  return result.data || [];
}

export async function saveUserDashboard(widgets: any[]): Promise<any[]> {
  const result = await request<any[]>('/api/user-dashboard', {
    method: 'PUT',
    body: JSON.stringify({ widgets }),
  });
  return result.data || [];
}

// ═══════════════════════════════════════════════════════════════
// Coaching notes API (E1)
// ═══════════════════════════════════════════════════════════════

export async function createCoachingNote(data: {
  operatorId: string;
  category?: string;
  title?: string;
  note: string;
}): Promise<any | null> {
  const result = await request<any>('/api/coaching-notes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.data || null;
}

// ═══════════════════════════════════════════════════════════════
// Badge check API (E5)
// ═══════════════════════════════════════════════════════════════

export async function checkBadges(operatorId: string): Promise<any | null> {
  const result = await request<any>(`/api/badges/check/${operatorId}`, {
    method: 'POST',
  });
  return result.data || null;
}

// ═══════════════════════════════════════════════════════════════
// Season close API (E4)
// ═══════════════════════════════════════════════════════════════

export async function closeSeason(seasonId: string): Promise<any | null> {
  const result = await request<any>(`/api/seasons/${seasonId}/close`, {
    method: 'POST',
  });
  return result.data || null;
}

// ═══════════════════════════════════════════════════════════════
// Approval workflow API (E3)
// ═══════════════════════════════════════════════════════════════

export async function resolveApproval(
  approvalId: string,
  action: 'approve' | 'reject' | 'contest',
  reason?: string,
): Promise<any | null> {
  const result = await request<any>(`/api/pending-approvals/${approvalId}`, {
    method: 'PUT',
    body: JSON.stringify({ action, reason }),
  });
  return result.data || null;
}

// ═══════════════════════════════════════════════════════════════
// Pipeline observability API (Módulo 8)
// ═══════════════════════════════════════════════════════════════

export interface PipelineStatus {
  auditCountsByStatus: Record<string, number>;
  avgCompletionHours: number;
  p95CompletionHours: number;
  completedLast7d: number;
  slaBreached: number;
  overdueApprovals: number;
  openFalhaGrave: number;
  costThisMonthUsd: number;
  generatedAt: string;
}

export async function getPipelineStatus(): Promise<PipelineStatus | null> {
  const result = await request<PipelineStatus>('/api/pipeline/status');
  return result.data || null;
}

// ═══════════════════════════════════════════════════════════════
// Upload API (I2)
// ═══════════════════════════════════════════════════════════════

// Message-bearing upload: surfaces the backend error (size limit, mime, etc.)
// instead of collapsing every failure to null.
export async function uploadFileResult(
  type: 'avatar' | 'audio' | 'logo',
  file: File,
): Promise<{ url?: string; filename?: string; error?: string }> {
  const token = getAccessToken();
  if (!token) return { error: 'Sessao invalida. Faca login novamente.' };

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${API_BASE_URL}/api/upload/${type}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    let json: any = {};
    try {
      json = await response.json();
    } catch {
      // Non-JSON body (e.g. reverse-proxy 413 HTML) — surface a generic message
      return { error: response.ok ? 'Resposta invalida do servidor.' : `Erro ${response.status} ao enviar arquivo.` };
    }
    if (!response.ok) return { error: json.error || `Erro ${response.status}` };
    return json.data || { error: 'Resposta invalida do servidor.' };
  } catch (error) {
    return { error: `Erro de rede: ${error}` };
  }
}

export async function uploadFile(
  type: 'avatar' | 'audio' | 'logo',
  file: File,
): Promise<{ url: string; filename: string } | null> {
  const res = await uploadFileResult(type, file);
  return res.url ? { url: res.url, filename: res.filename as string } : null;
}

// ─── Tenants ──────────────────────────────────────────────────

export interface TenantItem {
  id: string;
  name: string;
}

export async function listTenants(): Promise<TenantItem[]> {
  const result = await request<TenantItem[]>('/api/tenants');
  return (result.data as any[])?.map((t: any) => ({ id: t.id, name: t.name })) || [];
}

// ═══════════════════════════════════════════════════════════════
// Connectors API (Módulo 1 — Connector Hub)
// ═══════════════════════════════════════════════════════════════

export async function listConnectors(): Promise<Connector[]> {
  const result = await request<Connector[]>('/api/connectors');
  return result.data || [];
}

export async function createConnector(data: {
  name: string;
  connectorType: ConnectorType;
  config?: Record<string, unknown>;
  tenantId?: string;
}): Promise<Connector | null> {
  const result = await request<Connector>('/api/connectors', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (result.error) throw new Error(result.error);
  return result.data || null;
}

export async function updateConnector(
  id: string,
  data: Partial<{
    name: string;
    status: ConnectorStatus;
    config: Record<string, unknown>;
    webhookSecret: string;
  }>,
): Promise<Connector | null> {
  const result = await request<Connector>(`/api/connectors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (result.error) throw new Error(result.error);
  return result.data || null;
}

export async function listConnectorJobs(
  connectorId: string,
  page = 1,
  limit = 20,
): Promise<IngestJob[]> {
  const qs = toQueryString({ page, limit });
  const result = await request<IngestJob[]>(`/api/connectors/${connectorId}/jobs${qs}`);
  return result.data || [];
}

export async function uploadAudioToConnector(
  connectorId: string,
  file: File,
): Promise<{ jobId: string } | null> {
  const token = getAccessToken();
  if (!token) throw new Error('Sessao invalida. Faca login novamente.');

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/connectors/${connectorId}/ingest/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.error || `Erro ${response.status}`);
  }
  return json.data || null;
}

// ─── Field Mappings ───────────────────────────────────────────

export interface FieldMapping {
  id: number;
  connectorId: string;
  tenantId: string;
  sourceField: string;
  targetField: string;
  transform: 'none' | 'date_iso' | 'uppercase' | 'lowercase' | 'trim';
  createdAt: string;
}

export type FieldMappingTransform = FieldMapping['transform'];

export async function listFieldMappings(connectorId: string): Promise<FieldMapping[]> {
  const result = await request<FieldMapping[]>(`/api/connectors/${connectorId}/field-mappings`);
  return result.data || [];
}

export async function createFieldMapping(
  connectorId: string,
  data: { sourceField: string; targetField: string; transform?: FieldMappingTransform; tenantId?: string },
): Promise<FieldMapping | null> {
  const result = await request<FieldMapping>(`/api/connectors/${connectorId}/field-mappings`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (result.error) throw new Error(result.error);
  return result.data || null;
}

export async function updateFieldMapping(
  connectorId: string,
  mappingId: number,
  data: { sourceField?: string; targetField?: string; transform?: FieldMappingTransform },
): Promise<FieldMapping | null> {
  const result = await request<FieldMapping>(`/api/connectors/${connectorId}/field-mappings/${mappingId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (result.error) throw new Error(result.error);
  return result.data || null;
}

export async function deleteFieldMapping(connectorId: string, mappingId: number): Promise<boolean> {
  const result = await request<{ deleted: boolean }>(`/api/connectors/${connectorId}/field-mappings/${mappingId}`, {
    method: 'DELETE',
  });
  return result.data?.deleted ?? false;
}

export async function triggerConnectorSync(connectorId: string, tenantId?: string): Promise<{ ingested: number; skipped: number; errors: number } | null> {
  const result = await request<{ ingested: number; skipped: number; errors: number }>(`/api/connectors/${connectorId}/sync`, {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
  if (result.error) throw new Error(result.error);
  return result.data || null;
}

// ═══════════════════════════════════════════════════════════════
// Superadmin — Repositório de Auditorias & Treinamentos
// ═══════════════════════════════════════════════════════════════

export interface RepositorioResumo {
  totalAuditorias: number;
  totalTreinamentos: number;
  totalEmpresas: number;
  comAudio: number;
  comTranscricao: number;
  porEmpresa: Array<{ tenantId: string; empresa: string; auditorias: number; treinamentos: number }>;
}
export interface RepositorioAuditoria {
  id: string; tenantId: string; empresa: string; operador: string | null;
  callId: string; data: string; duracao: number; score: number | null;
  status: string; temAudio: boolean; audioUrl: string | null; temTranscricao: boolean;
}
export interface RepositorioTreino {
  id: string; tenantId: string; empresa: string; operador: string | null;
  cenario: string | null; data: string; duracao: number;
  status: string; temGravacao: boolean; temTranscricao: boolean;
}
export interface RepositorioResponse {
  resumo: RepositorioResumo;
  auditorias: RepositorioAuditoria[];
  treinamentos: RepositorioTreino[];
}

export async function getSuperadminRepository(empresa?: string): Promise<RepositorioResponse> {
  const qs = empresa ? `?empresa=${encodeURIComponent(empresa)}` : '';
  const result = await request<RepositorioResponse>(`/api/superadmin/repositorio${qs}`);
  if (result.error || !result.data) {
    throw new Error(result.error || 'Falha ao carregar repositório');
  }
  return result.data;
}

async function downloadAuthedFile(path: string, filename: string): Promise<void> {
  const token = getAccessToken();
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Erro ${response.status} ao baixar ${filename}`);
  }
  const blob = await response.blob();
  saveAs(blob, filename);
}

export function downloadAuditTranscript(id: string): Promise<void> {
  return downloadAuthedFile(`/api/superadmin/repositorio/auditorias/${id}/transcricao`, `auditoria_${id}.txt`);
}
export function downloadTrainingTranscript(id: string): Promise<void> {
  return downloadAuthedFile(`/api/superadmin/repositorio/treinamentos/${id}/transcricao`, `treinamento_${id}.txt`);
}
export function downloadAuditAudio(id: string, audioUrl: string): Promise<void> {
  const ext = (audioUrl.split('.').pop() || 'audio').split('?')[0];
  return downloadAuthedFile(audioUrl, `auditoria_${id}.${ext}`);
}
export function downloadTrainingRecording(id: string): Promise<void> {
  return downloadAuthedFile(`/api/training-sessions/${id}/recording/file`, `treinamento_${id}.webm`);
}
